import type {
  CopilotProposal,
  CopilotProposalId,
} from "@repo/app-types/entities/copilotProposal";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { useConfirmationModal } from "@repo/ui/hooks/useConfirmationModal";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { IconChevronLeft } from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { useResolveProposalMutation } from "../../../api/entities/copilotProposal.js";

import { useDeleteKnowledgeBaseAttachmentMutation } from "../../../api/entities/knowledgeBase.js";
import { isConflictQueryError } from "../../../api/queryErrors.js";
import { ResourceSaveFooter } from "../../../components/resourceSaveFooter.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import { useUnsavedChangesGuard } from "../../../hooks/useUnsavedChangesGuard.js";
import { AttachmentEditorContent } from "../../agents/components/attachmentEditorContent.js";
import { useAttachmentEditorState } from "../../agents/components/useAttachmentEditorState.js";
import { useDeepLinkedProposal } from "../../copilot/copilotDeepLinks.js";
import { useReportEditorProposal } from "../../copilot/editorProposalsSlice.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { KnowledgeBaseSyncPill } from "./knowledgeBaseSyncPill.js";

/**
 * An Ask Alpharun proposal waiting for the document to load. Its content
 * becomes the editor's draft, and it is recorded as applied once that draft is
 * saved.
 */
type PendingRewrite = CopilotProposal<"knowledgeBaseAttachment">;

interface KnowledgeBaseAttachmentEditorProps {
  knowledgeBaseId: string;
  attachmentId: string;
  /** Known read-only state from the parent knowledge base while the document loads. */
  isReadOnly?: boolean;
  onClose: () => void;
}

export const KnowledgeBaseAttachmentEditor: FC<
  KnowledgeBaseAttachmentEditorProps
> = (props) => {
  const { knowledgeBaseId, attachmentId, isReadOnly, onClose } = props;
  const dispatch = useAppDispatch();

  const {
    attachment,
    isLoading,
    isSaving,
    isInitialized,
    editedBody,
    setEditedBody,
    fetchedBody,
    hasUnsavedChanges,
    onSave,
    onDiscard,
    selectedTabIndex,
    setSelectedTabIndex,
  } = useAttachmentEditorState({ knowledgeBaseId, attachmentId });
  const { guardModal, navigateWithoutConfirmation } =
    useUnsavedChangesGuard(hasUnsavedChanges);

  const [pendingRewrite, setPendingRewrite] = useState<PendingRewrite | null>(
    null,
  );
  // The proposal whose content is in the unsaved body.
  const [linkedProposalId, setLinkedProposalId] = useState<
    CopilotProposalId | undefined
  >();
  const [resolveProposal] = useResolveProposalMutation();

  const notifyProposalUnavailable = useCallback(() => {
    showNotification(dispatch, {
      id: `attachment-proposal-unavailable-${Date.now().toString()}`,
      type: NotificationType.FAILURE,
      primaryMessage: "This suggestion is no longer available.",
    });
  }, [dispatch]);
  // One-shot link from an Ask Alpharun card (?proposalId=...): the pending
  // proposal's content becomes the draft as soon as both have loaded. A card
  // for another document loads nothing.
  const { row: deepLinkProposal, clear: clearDeepLinkProposalId } =
    useDeepLinkedProposal("knowledgeBaseAttachment", {
      onUnavailable: notifyProposalUnavailable,
    });
  useEffect(() => {
    if (!deepLinkProposal) {
      return;
    }
    clearDeepLinkProposalId();
    if (deepLinkProposal.resource_id === attachmentId) {
      setPendingRewrite(deepLinkProposal);
    }
  }, [deepLinkProposal, attachmentId, clearDeepLinkProposalId]);

  // The chat marks the card this editor holds, and reads Saving while it saves.
  useReportEditorProposal(pendingRewrite?.id ?? linkedProposalId, isSaving);

  // The proposal's content becomes the draft once the document has loaded, and
  // stays linked until Save records the proposal as applied. The Diff tab is
  // the review: it compares the draft to the document as saved, so anything
  // that changed since the rewrite was drafted shows up there.
  const appliedRewriteRef = useRef<PendingRewrite | null>(null);
  useEffect(() => {
    if (
      !pendingRewrite ||
      !isInitialized ||
      appliedRewriteRef.current === pendingRewrite
    ) {
      return;
    }
    appliedRewriteRef.current = pendingRewrite;
    setPendingRewrite(null);

    setEditedBody(pendingRewrite.data.body);
    setSelectedTabIndex(1); // Switch to "Diff" tab
    setLinkedProposalId(pendingRewrite.id);
  }, [pendingRewrite, isInitialized, setEditedBody, setSelectedTabIndex]);

  const [deleteAttachment, { isLoading: isDeleting }] =
    useDeleteKnowledgeBaseAttachmentMutation();

  const handleSave = useCallback(async () => {
    try {
      await onSave();
      showNotification(dispatch, {
        id: `attachment-save-${Date.now().toString()}`,
        type: NotificationType.SUCCESS,
        primaryMessage: "Article saved",
        secondaryMessage: "Your changes have been saved.",
      });
    } catch {
      showNotification(dispatch, {
        id: `attachment-save-error-${Date.now().toString()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Failed to save article",
        secondaryMessage: "Please try again.",
      });
      return;
    }
    // The saved body carried the suggestion, so it is applied. One that has
    // moved on (409) was already decided; the save stands either way. Any other
    // failure keeps the suggestion linked, so the next Save retries it.
    if (!linkedProposalId) {
      return;
    }
    try {
      await resolveProposal({
        proposalId: linkedProposalId,
        status: "applied",
      }).unwrap();
    } catch (err) {
      if (!isConflictQueryError(err)) {
        showNotification(dispatch, {
          id: `attachment-proposal-resolve-error-${Date.now().toString()}`,
          type: NotificationType.FAILURE,
          primaryMessage:
            "The article was saved, but the suggestion could not be marked as applied.",
        });
        return;
      }
      notifyProposalUnavailable();
    }
    setLinkedProposalId(undefined);
  }, [
    onSave,
    dispatch,
    linkedProposalId,
    resolveProposal,
    notifyProposalUnavailable,
  ]);

  const handleDelete = useCallback(async () => {
    try {
      await deleteAttachment({ knowledgeBaseId, attachmentId }).unwrap();
      showNotification(dispatch, {
        id: `delete-success-${attachmentId}`,
        primaryMessage: `Successfully deleted "${attachment?.title || attachment?.file_name || ""}" document`,
        type: NotificationType.SUCCESS,
      });
      navigateWithoutConfirmation(onClose);
    } catch (err: any) {
      showNotification(dispatch, {
        id: `delete-error-${attachmentId}`,
        primaryMessage: `Failed to delete document: ${err.data?.error || err.message || "Unknown error"}`,
        type: NotificationType.FAILURE,
      });
    }
  }, [
    deleteAttachment,
    knowledgeBaseId,
    attachmentId,
    attachment,
    dispatch,
    navigateWithoutConfirmation,
    onClose,
  ]);

  const {
    renderConfirmationModal: renderDeleteModal,
    onConfirmThenClose: onConfirmThenDelete,
  } = useConfirmationModal({
    needsConfirmation: true,
    onClose: handleDelete,
  });

  // Keep the state unknown until either the document or its parent KB has
  // loaded. Treating undefined as editable flashes the AI assistant while a
  // synced document is in flight.
  const isSynced = attachment ? Boolean(attachment.sync) : isReadOnly;
  const documentTitle = attachment
    ? attachment.title || attachment.file_name || "Untitled document"
    : undefined;
  return (
    <div className="flex h-full flex-col">
      {guardModal}
      {renderDeleteModal({
        title: "Delete document?",
        message:
          "Are you sure you want to delete this document? This action cannot be undone.",
        confirmText: "Delete",
      })}
      <PageHeader
        className="pb-3"
        title={documentTitle}
        isLoading={!documentTitle}
        leadingAction={
          <IconButton
            accessibleLabel="Back to documents"
            variant={ButtonVariant.Tertiary}
            onClick={onClose}
            size={Size.SMALL}
            icon={<IconChevronLeft aria-hidden className="size-4" />}
          />
        }
        actions={
          attachment?.sync ? (
            <KnowledgeBaseSyncPill
              provider={attachment.sync.provider}
              lastSyncedAt={attachment.sync.synced_at}
            />
          ) : isSynced === false ? (
            <Button
              variant={ButtonVariant.Warning}
              onClick={onConfirmThenDelete}
              isDisabled={isDeleting}
              size={Size.SMALL}
            >
              Delete
            </Button>
          ) : undefined
        }
      />
      <div className="flex min-h-0 flex-1">
        <AttachmentEditorContent
          attachment={attachment}
          isLoading={isLoading}
          isSaving={isSaving}
          isReadOnly={isSynced}
          showTitle={false}
          editedBody={editedBody}
          savedBody={fetchedBody}
          setEditedBody={setEditedBody}
          selectedTabIndex={selectedTabIndex}
          onSelectedTabIndexChange={setSelectedTabIndex}
        />
      </div>
      {isSynced === false && (hasUnsavedChanges || linkedProposalId) ? (
        <ResourceSaveFooter
          onCancel={() => {
            onDiscard();
            setLinkedProposalId(undefined);
          }}
          onSave={() => {
            void handleSave();
          }}
          isSaving={isSaving}
        />
      ) : null}
    </div>
  );
};
